import { JAVA_API_STATUS } from '../../utils/axios-interceptor';
import { fetchStatusTimeline } from './StatusTimelineApiService';
import { StatusTimeline, StatusTimelineEntry, StatusDropdownOption } from '../../types/statusTimeline';

// Interface for the API response structure
interface AuditLogEntry {
    id: string;
    changeUserId: string;
    changeDate: number;
    type: string;
    data: {
        previousStatus: string;
        newStatus: string;
    };
    referenceId: string;
    orgId: string;
    isDeleted: boolean;
    createdAt?: number; // Present in actual API response
}

interface AuditLogResponse {
    responseData: {
        responseCode: number;
        responseMessage: string;
    };
    data: AuditLogEntry[];
    currentPageNumber?: number;
    hasNextPage?: boolean;
    retryCount?: number;
    taskTrackerAlreadyExists?: boolean;
    dropdownOptions?: Record<string, StatusDropdownOption>; // Status mapping from API
}

// Helper function to build timeline from audit log entries
const buildTimelineFromAuditLogs = (
    auditLogs: AuditLogEntry[],
    dropdownOptions?: Record<string, StatusDropdownOption>,
): StatusTimelineEntry[] => {
    // Filter out deleted entries and sort by createdAt to get chronological order
    const sortedLogs = [...auditLogs].sort((a, b) => a.changeDate - b.changeDate);

    const timeline: StatusTimelineEntry[] = [];

    for (const log of sortedLogs) {
        // Add entry for the new status
        const statusValue = log.data.newStatus;
        const statusOption = dropdownOptions?.[statusValue];
        const statusName = statusOption?.label || statusValue;
        const statusLabelColor = statusOption?.labelColor || '#6021b3'; // Fallback color
        const statusColor = statusOption?.backgroundColor || '#6021b3'; // Fallback color

        // Log for debugging when dropdown options are missing
        if (!dropdownOptions || !statusOption) {
            console.warn(`Status dropdown option missing for status: ${statusValue}. Using fallback colors.`);
        }

        timeline.push({
            statusName,
            statusValue,
            enteredAt: log.changeDate ? new Date(log.changeDate).toISOString() : new Date().toISOString(),
            userId: log.changeUserId,
            statusColor,
            statusLabelColor,
        });
    }

    return timeline;
};

// Helper function to convert deals dropdown options to timeline format
const convertDealsDropdownOptions = (dealsOptions: StatusDropdownOption[]): Record<string, StatusDropdownOption> => {
    const converted: Record<string, StatusDropdownOption> = {};

    dealsOptions.forEach((option) => {
        converted[option.value] = {
            label: option.label,
            value: option.value,
            backgroundColor: option.backgroundColor,
            labelColor: option.labelColor,
            customCaret: '▼', // Default caret since timeline doesn't need the JSX element
        };
    });

    return converted;
};

export const getStatusTimeline = async (payload: {
    referenceId: string;
    recordType: 'LEAD' | 'DEAL' | 'MANDATE';
    companyName: string;
    baseURL?: string;
    dealStatusDropdownOptions?: StatusDropdownOption[];
}): Promise<StatusTimeline | false> => {
    try {
        // Use different endpoint for mandate view
        const endpoint = payload.recordType === 'MANDATE' ? 'unifier/getAuditLogs' : 'account/getAuditLogs';

        const response = await fetchStatusTimeline(
            {
                referenceId: payload.referenceId,
                auditLogType: 'STATUS_UPDATE',
            },
            payload.baseURL,
            endpoint,
        );

        // Handle different response structures - deals data is wrapped in responseBody
        const apiResponse: AuditLogResponse = payload.recordType === 'MANDATE' ? response.data.responseBody : response.data;

        if (apiResponse.responseData.responseCode === JAVA_API_STATUS.SUCCESS) {
            // Use hardcoded dropdown options for mandate view, API response dropdownOptions for others
            const dropdownOptions =
                payload.recordType === 'MANDATE'
                    ? convertDealsDropdownOptions(payload.dealStatusDropdownOptions as StatusDropdownOption[])
                    : apiResponse.dropdownOptions;

            // Handle case where data array is empty
            if (!apiResponse.data || apiResponse.data.length === 0) {
                console.warn('No status timeline data found for reference ID:', payload.referenceId);
                return {
                    recordId: payload.referenceId,
                    recordType: payload.recordType,
                    companyName: payload.companyName,
                    timeline: [],
                    lastStatusUpdate: {
                        timestamp: new Date().toISOString(),
                        elapsedTime: 'No status updates',
                    },
                    dropdownOptions,
                };
            }

            const timeline = buildTimelineFromAuditLogs(apiResponse.data, dropdownOptions);

            // Get the last status update info
            const lastEntry = timeline[timeline.length - 1];
            const lastUpdateTimestamp = lastEntry?.enteredAt || new Date().toISOString();

            // Calculate elapsed time (simplified for now)
            const now = new Date();
            const lastUpdate = new Date(lastUpdateTimestamp);
            const diffMs = now.getTime() - lastUpdate.getTime();
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const elapsedTime = diffDays > 0 ? `${diffDays} day${diffDays > 1 ? 's' : ''} ago` : 'Today';

            return {
                recordId: payload.referenceId,
                recordType: payload.recordType,
                companyName: payload.companyName,
                timeline,
                lastStatusUpdate: {
                    timestamp: lastUpdateTimestamp,
                    elapsedTime,
                },
                dropdownOptions, // Include dropdown options in response
            };
        } else {
            console.error('API Error:', apiResponse.responseData.responseMessage);
            return false;
        }
    } catch (err) {
        console.error('Error fetching status timeline:', err);
        return false;
    }
};
